import req from '../../api/request'
import type { GamePatch } from '../../features/game/gameListView/bulkPlan'
import type { GameLike } from '../../features/game/gameListView/types'

/**
 * One transport attempt, and no global error banner.
 *
 * Bulk retiming reconciles and reports failures itself. Leaving the defaults in
 * place would turn a three-poll reconciliation into nine transport attempts and
 * stack a modal per failed row on top of the panel that already lists them.
 */
const BULK_REQUEST_SETTINGS = {
  maxAttempts: 1,
  reportGlobalError: false,
} as const

/**
 * PUT /games/:gameId
 *
 * The response is deliberately NOT usable as a list-row replacement:
 * `GameResource` omits the venue's facilities that `GamesResource` includes, so
 * swapping a grid row for it would strip the surface options out from under the
 * editor. Callers patch their cached row from the patch they sent instead.
 */
export const updateGame = async (
  gameId: string,
  patch: GamePatch
): Promise<GameLike> => {
  const { data } = await req(`/games/${gameId}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
    ...BULK_REQUEST_SETTINGS,
  })
  return data as GameLike
}

/**
 * GET /games/:gameId — the reconciliation read.
 *
 * Used only to establish what actually happened after a PUT whose outcome is
 * unknown (5xx, 504, dropped connection). `GamesController::update` saves the
 * game before doing roster work with no transaction around the pair, so the
 * absence of a clean response says nothing about whether the row was written.
 */
export const getGame = async (gameId: string): Promise<GameLike> => {
  const { data } = await req(`/games/${gameId}`, BULK_REQUEST_SETTINGS)
  return data as GameLike
}

/*
 * ─── Add/Edit Game modal ───────────────────────────────────────────────────
 *
 * `maxAttempts: 1` on every one of these: they are NOT idempotent, and `req`
 * defaults to 3 transport attempts. A create that times out after the server
 * already wrote the row would be retried and produce duplicate games; the same
 * applies to a PUT racing a concurrent edit, and to a DELETE whose second
 * attempt 404s on a row it just removed. One attempt, and the modal surfaces
 * the failure for the user to retry deliberately.
 *
 * `reportGlobalError` stays ON (unlike `BULK_REQUEST_SETTINGS`) — the modal
 * shows the API's field errors inline but still wants the standard banner for
 * transport failures.
 *
 * These return the full `{ data }` envelope rather than unwrapping it, because
 * the existing success banners read `data.league_game_number` off the resolved
 * value (`components/Form/banner-helpers.js:92`).
 */

/** One transport attempt — these writes must never be replayed. */
const SINGLE_ATTEMPT = { maxAttempts: 1 } as const

export interface GameMutationResponse {
  data: GameLike
}

/** POST /schedules/:scheduleId/games */
export const createGame = async (
  scheduleId: string,
  body: Record<string, unknown>
): Promise<GameMutationResponse> =>
  (await req(`/schedules/${scheduleId}/games`, {
    method: 'POST',
    body: JSON.stringify(body),
    ...SINGLE_ATTEMPT,
  })) as GameMutationResponse

/** PUT /games/:gameId — the modal's save, distinct from the bulk `updateGame`. */
export const saveGame = async (
  gameId: string,
  body: Record<string, unknown>
): Promise<GameMutationResponse> =>
  (await req(`/games/${gameId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
    ...SINGLE_ATTEMPT,
  })) as GameMutationResponse

/** DELETE /games/:gameId */
export const deleteGame = async (gameId: string): Promise<void> => {
  await req(`/games/${gameId}`, { method: 'DELETE', ...SINGLE_ATTEMPT })
}
